import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDynamicTranslation } from '@monorepo/client-common';
import { GroupType, Program } from '@monorepo/types';
import { getEnv } from '@hub/helpers/getters.store';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { useQuery } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.PROGRAMS;

const programsQueryKey = QUERY_KEYS[CONFIG_KEY].ALL;

const programsQueryStaleTime = QUERY_CONFIGS[CONFIG_KEY].staleTime;

/** Program list item: `name` is translated for display; `englishName` is the API source string for stable matching. */
export interface TranslatedProgram extends Program {
  englishName: string;
}

/** Group list item: `name` is translated for display; `englishName` is the API source string for stable matching. */
export interface TranslatedGroup extends GroupType {
  englishName: string;
}

/**
 * Fetches programs and groups from the program API (single GET used by all program/group queries).
 */
async function loadProgramsAndGroups(): Promise<{
  programs: Program[];
  groups: GroupType[];
}> {
  const {
    apiFactory: { programService },
  } = getEnv();

  return programService.getProgramsAndGroups();
}

/**
 * TanStack Query hook for the programs list.
 *
 * Shares query key and cache with {@link useGroupsData} (one network response, `select` on `programs`).
 *
 * @returns Query result whose `data` is `Program[] | undefined`.
 */
export const useProgramsData = () => {
  return useQuery({
    queryKey: programsQueryKey,
    queryFn: loadProgramsAndGroups,
    staleTime: programsQueryStaleTime,
    select: (data) => data.programs,
  });
};

/**
 * TanStack Query hook for the groups list.
 *
 * Shares query key and cache with {@link useProgramsData} (one network response, `select` on `groups`).
 *
 * @returns Query result whose `data` is `GroupType[] | undefined`.
 */
export const useGroupsData = () => {
  return useQuery({
    queryKey: programsQueryKey,
    queryFn: loadProgramsAndGroups,
    staleTime: programsQueryStaleTime,
    select: (data) => data.groups,
  });
};

/**
 * Programs from {@link useProgramsData} with each `name` passed through {@link getDynamicTranslation}.
 *
 * `englishName` preserves the API `name` so callers can match backend English strings while using
 * translated `name` for display.
 *
 * @returns Mapped programs, or `undefined` while loading / if data is missing.
 */
export const useTranslatedProgramsData = ():
  | TranslatedProgram[]
  | undefined => {
  const programs = useProgramsData();
  const { i18n, t } = useTranslation();

  return useMemo(
    () =>
      programs.data?.map((program): TranslatedProgram => {
        const englishName = program.name;
        const translatedName = getDynamicTranslation(
          'name',
          program,
          t as TFunction,
          i18n.language,
        );
        return {
          ...program,
          englishName,
          name: translatedName,
        };
      }),
    [programs.data, i18n.language, t],
  );
};

/**
 * Groups from {@link useGroupsData} with each `name` passed through {@link getDynamicTranslation}.
 *
 * `englishName` preserves the API `name` so callers can match backend English strings while using
 * translated `name` for display.
 *
 * @returns Mapped groups, or `undefined` while loading / if data is missing.
 */
export const useTranslatedGroupsData = (): TranslatedGroup[] | undefined => {
  const groups = useGroupsData();
  const { i18n, t } = useTranslation();

  return useMemo(
    () =>
      groups.data?.map((group): TranslatedGroup => {
        const englishName = group.name;
        const translatedName = getDynamicTranslation(
          'name',
          group,
          t as TFunction,
          i18n.language,
        );
        return {
          ...group,
          englishName,
          name: translatedName,
        };
      }),
    [groups.data, i18n.language, t],
  );
};

/**
 * Resolves a display name from {@link useTranslatedProgramsData} by matching the API source name.
 *
 * @param englishProgramName - Backend / English program `name` to match against {@link TranslatedProgram.englishName}.
 * @returns Matched translated {@link Program.name}, or `englishProgramName` if none match or data is not loaded.
 */
export const useTranslatedProgramName = (
  englishProgramName: string,
): string => {
  const translatedPrograms = useTranslatedProgramsData();

  return useMemo(
    () =>
      translatedPrograms?.find(
        (program) => program.englishName === englishProgramName,
      )?.name ?? englishProgramName,
    [translatedPrograms, englishProgramName],
  );
};

/**
 * Resolves a display name from {@link useTranslatedGroupsData} by matching the API source name.
 *
 * @param englishGroupName - Backend / English group `name` to match against {@link TranslatedGroup.englishName}.
 * @returns Matched translated {@link GroupType.name}, or `englishGroupName` if none match or data is not loaded.
 */
export const useTranslatedGroupName = (englishGroupName: string): string => {
  const translatedGroups = useTranslatedGroupsData();

  return useMemo(
    () =>
      translatedGroups?.find((group) => group.englishName === englishGroupName)
        ?.name ?? englishGroupName,
    [translatedGroups, englishGroupName],
  );
};
